import Slider from 'react-slick'
import styles from './AutoSlider.module.scss'
import styled from 'styled-components'
import Link from 'next/link'
import React, { useEffect, useRef } from 'react'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import { gsap, Power1 } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const tweenVars: gsap.TweenVars = {
  y: '50',
  duration: 0.5,
  opacity: 0,
  ease: Power1.easeOut,
}

interface CaseStudyItem {
  projectName: string
  client: string
  description?: string
  src: string
  alt: string
  id: string
  tags: [
    ...{
      tag: string
      href: string
    }[],
  ]
  isOneline: boolean
  linkToDetail?: string
}

const caseStudyItems: CaseStudyItem[] = [
  {
    projectName: 'HeartOneポータルアプリ',
    client: '大和ハウスフィナンシャル株式会社',
    src: '/images/img/case/img_thum_heartone.webp',
    alt: 'HeartOneポータルアプリ',
    id: 'heartone',
    tags: [
      {
        tag: 'アプリケーション開発',
        href: '/cases/application-development/',
      },
      {
        tag: 'クラウドネイティブ',
        href: '/cases/cloudnative/',
      },
      {
        tag: 'UX/UI改善',
        href: '/cases/uxui-improvement/',
      },
      {
        tag: 'ZEBRA',
        href: '/cases/zebra/',
      },
      { tag: '銀行/保険/証券', href: '/cases/finance/' },
    ],
    isOneline: false,
    linkToDetail: '/cases/heart-one',
  },
  {
    projectName: 'ファイル転送ミドルウェア HULFT',
    client: '株式会社セゾンテクノロジー',
    src: '/images/img/case/img_thum_hulft.webp',
    alt: 'ファイル転送ミドルウェア HULFT',
    id: 'hulft',
    tags: [
      {
        tag: 'UX/UI改善',
        href: '/cases/uxui-improvement/',
      },
      {
        tag: 'プロトタイピング・仮説検証',
        href: '/cases/prototyping/',
      },
      { tag: 'IT/情報/通信', href: '/cases/telecommunications/' },
    ],
    isOneline: false,
    linkToDetail: '/cases/hulft',
  },
  {
    projectName: 'マクロミル デザインシステム',
    client: '株式会社マクロミル',
    src: '/images/img/case/img_thum_macromill-designsystem.webp',
    alt: 'マクロミル デザインシステム',
    id: 'macromilldesignsystem',
    tags: [
      {
        tag: 'デザインシステム構築',
        href: '/cases/design-system',
      },
      { tag: 'IT/情報/通信', href: '/cases/telecommunications/' },
    ],
    isOneline: false,
    linkToDetail: '/cases/macromill-designsystem',
  },
  {
    projectName: 'マーケティングデータプラットフォーム Coreka',
    client: '株式会社マクロミル',
    src: '/images/img/case/img_thum_coreka.webp',
    alt: 'マーケティングデータプラットフォーム Coreka',
    id: 'coreka',
    tags: [
      {
        tag: 'UX/UI改善',
        href: '/cases/uxui-improvement',
      },
      { tag: 'IT/情報/通信', href: '/cases/telecommunications/' },
    ],
    isOneline: false,
    linkToDetail: '/cases/coreka',
  },
  {
    projectName: 'ぐるっとAI見積り',
    client: 'アート引越センター株式会社',
    src: '/images/img/case/img_thum_guruttoai.webp',
    alt: 'ぐるっとAI見積り',
    id: 'guruttoai',
    tags: [
      {
        tag: 'アプリケーション開発',
        href: '/cases/application-development/',
      },
      {
        tag: 'クラウドネイティブ',
        href: '/cases/cloudnative/',
      },
      {
        tag: 'UX/UI改善',
        href: '/cases/uxui-improvement/',
      },
      { tag: '小売/流通/物流', href: '/cases/retail/' },
    ],
    isOneline: false,
    linkToDetail: '/cases/gurutto-ai',
  },
  {
    projectName: '共済マイページ',
    client: '日本コープ共済生活共同組合連合会',
    src: '/images/img/case/img_thum_kyosaimypage.png',
    alt: '共済マイページ',
    id: 'kyosaimypage',
    tags: [
      {
        tag: 'アプリケーション開発',
        href: '/cases/application-development/',
      },
      {
        tag: 'フロントエンド開発',
        href: '/cases/frontend-dev/',
      },
      {
        tag: 'UX/UI改善',
        href: '/cases/uxui-improvement/',
      },
      { tag: '銀行/保険/証券', href: '/cases/finance/' },
    ],
    isOneline: false,
    linkToDetail: '/cases/kyosaimypage',
  },
]

const CustomSlide: React.FC<{ item: CaseStudyItem }> = ({ item }) => (
  <a href={item.linkToDetail} className={styles.casescard}>
    <div className={styles.casescard_img}>
      <img src={item.src} alt={item.alt} />
    </div>
    <div className={styles.casescard_label}>
      <p className={styles.casescard_label_name}>{item.projectName}</p>
      <p className={styles.casescard_label_client}>{item.client}</p>
      <ul className={styles.casescard_label_category}>
        {item.tags.map((tagItem, index) => (
          <li
            key={`${tagItem.tag}-${index}`}
            className={`${
              ['IT/情報/通信', 'メーカー/製造/建設', '銀行/保険/証券', '小売/流通/物流', 'その他'].includes(tagItem.tag)
                ? styles.casescard_label_category_white
                : styles.casescard_label_category_gray
            }`}
          >
            <a href={tagItem.href} className={styles.categoryButton} onClick={(e) => e.stopPropagation()}>
              {tagItem.tag}
            </a>
          </li>
        ))}
      </ul>
    </div>
  </a>
)

const AutoSlider: React.FC = () => {
  const sliderRef = useRef(null)
  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    arrows: false,
    autoplaySpeed: 8000,
    responsive: [
      {
        breakpoint: 1440,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 1000,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 720,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 620,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
    onInit: () => setEqualHeight(),
    onReInit: () => setEqualHeight(),
  }

  const setEqualHeight = () => {
    if (!sliderRef.current) return
    let maxHeight = 0
    const slides = sliderRef.current.querySelectorAll('.slick-slide')
    slides.forEach((slide) => {
      slide.style.height = 'auto'
      const height = slide.clientHeight
      maxHeight = Math.max(maxHeight, height)
    })
    slides.forEach((slide) => {
      slide.style.height = `${maxHeight}px`
    })
  }

  // スライダーのアニメーション
  useEffect(() => {
    const timeline = gsap.timeline()

    if (sliderRef.current) {
      timeline.from(sliderRef.current, tweenVars)

      ScrollTrigger.create({
        animation: timeline,
        trigger: sliderRef.current,
        start: 'top 90%',
        toggleActions: 'play none none reverse',
      })
    }
  }, [sliderRef])

  useEffect(() => {
    setEqualHeight()
    window.addEventListener('resize', setEqualHeight)
    return () => {
      window.removeEventListener('resize', setEqualHeight)
    }
  }, [])

  return (
    <SliderContainer>
      <SliderWrapper className={styles.slider} ref={sliderRef}>
        <Slider {...settings}>
          {caseStudyItems.map((item) =>
            item.linkToDetail ? (
              <Link href={item.linkToDetail} key={item.id}>
                <CustomSlide item={item} />
              </Link>
            ) : (
              <CustomSlide item={item} key={item.id} />
            ),
          )}
        </Slider>
      </SliderWrapper>
    </SliderContainer>
  )
}

const SliderContainer = styled.div`
  width: 100%;
  overflow: hidden;
  margin: 64px 0;
  @media (max-width: 960px) {
    margin: 48px 0;
  }
`

const SliderWrapper = styled.div`
  width: 100%;
  padding: 0 24px;
  @media (min-width: 720px) and (max-width: 1199px) {
    padding: 0 40px;
  }
  @media (min-width: 1200px) {
    width: 82%;
    max-width: 1180px;
    margin: 0 auto;
    padding: 0;
  }

  .slick-dots {
    margin: 20px auto 0;
    position: relative;
    bottom: 0;
    text-align: left;
    @media (max-width: 1199px) {
    }
    @media (min-width: 1200px) {
      margin: 27px auto 0;
    }
  }

  .slick-dots li {
    position: relative;
    width: 40px;
    height: 24px;
    margin: 0 0 0 8px;
    &::after {
      content: '';
      position: absolute;
      width: 100%;
      height: 4px;
      top: 0;
      bottom: 0;
      margin: auto 0;
      background: #ccc;
      pointer-events: none;
    }
    @media (max-width: 719px) {
      width: 32px;
    }
  }

  .slick-dots li:first-child {
    margin: 0;
  }

  .slick-dots li.slick-active::after {
    background: #db0032;
  }

  .slick-dots li button {
    width: 100%;
    height: 100%;
    padding: 0;
    background: transparent;
  }

  .slick-dots li button::before {
    content: none;
  }
  .slick-slide > div,
  .slick-slide > div > a {
    height: 100%;
  }
  .slick-list {
    overflow: visible;
  }
`

export default AutoSlider
